<template>
  <div class="bg-white rounded-lg shadow-md">
    <div class="p-4 border-b border-secondary-200">
      <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h3 class="text-lg font-bold font-serif text-secondary-700">
          {{ $t('sales_dashboard.contracts') }}
        </h3>
        
        <!-- Search and filters -->
        <div class="flex flex-col md:flex-row gap-2">
          <div class="relative">
            <BaseIcon 
              icon="search" 
              class="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400 w-4 h-4" 
            />
            <input 
              v-model="searchQuery"
              type="text"
              :placeholder="$t('sales_dashboard.search_contracts')"
              class="pl-10 pr-4 py-2 border border-secondary-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent w-full md:w-64"
            />
          </div>
          
          <select 
            v-model="statusFilter"
            class="px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">{{ $t('sales_dashboard.all_statuses') }}</option>
            <option value="active">{{ $t('sales_dashboard.status_active') }}</option>
            <option value="active_old">{{ $t('sales_dashboard.status_active_old') }}</option>
            <option value="pending">{{ $t('sales_dashboard.status_pending') }}</option>
            <option value="returned">{{ $t('sales_dashboard.status_returned') }}</option>
            <option value="dismissed">{{ $t('sales_dashboard.status_dismissed') }}</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="overflow-x-auto">
      <table class="w-full">
        <thead>
          <tr class="bg-secondary-50 border-b border-secondary-200">
            <th 
              v-for="column in columns" 
              :key="column.key"
              class="text-left py-3 px-4 text-xs font-semibold text-secondary-600 uppercase tracking-wide cursor-pointer hover:bg-secondary-100"
              @click="sortBy(column.key)"
            >
              <div class="flex items-center gap-1">
                {{ column.label }}
                <BaseIcon 
                  v-if="sortColumn === column.key"
                  :icon="sortOrder === 'asc' ? 'sort-up' : 'sort-down'" 
                  class="w-3 h-3"
                />
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="contract in displayedContracts" 
            :key="contract.id"
            class="border-b border-secondary-100 hover:bg-secondary-50 transition-colors"
          >
            <td class="py-3 px-4">
              <span class="font-medium text-primary-600">{{ contract.agreementNumber || '-' }}</span>
            </td>
            <td class="py-3 px-4">
              <span class="font-medium text-secondary-700">{{ contract.customerName }}</span>
            </td>
            <td class="py-3 px-4">
              <span 
                class="px-2 py-1 text-xs font-medium rounded-full"
                :class="getCategoryClass(contract.category)"
              >
                {{ getCategoryLabel(contract.category) }}
              </span>
            </td>
            <td class="py-3 px-4 text-right">
              {{ contract.category === 'cash' || contract.category === 'cash_services' 
                ? formatCurrency(contract.cashValue) 
                : formatCurrency(contract.monthlyValue) + $t('sales_dashboard.per_month_short') 
              }}
            </td>
            <td class="py-3 px-4">
              {{ formatDate(contract.startDate) }}
            </td>
            <td class="py-3 px-4">
              {{ contract.seller }}
            </td>
            <td class="py-3 px-4">
              <span 
                class="px-2 py-1 text-xs font-medium rounded-full"
                :class="getStatusClass(contract.status)"
              >
                {{ getStatusLabel(contract.status) }}
              </span>
            </td>
            <td class="py-3 px-4 text-center">
              {{ contract.productCount }}
            </td>
          </tr>
          
          <tr v-if="displayedContracts.length === 0">
            <td colspan="8" class="py-8 text-center text-secondary-400">
              {{ $t('sales_dashboard.no_contracts_found') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="p-4 border-t border-secondary-200 flex items-center justify-between">
      <div class="text-sm text-secondary-500">
        {{ $t('sales_dashboard.showing_contracts', { 
          from: (currentPage - 1) * pageSize + 1, 
          to: Math.min(currentPage * pageSize, totalContracts),
          total: totalContracts 
        }) }}
      </div>
      
      <div class="flex items-center gap-2">
        <button 
          @click="currentPage = 1"
          :disabled="currentPage === 1"
          class="px-2 py-1 text-sm border border-secondary-200 rounded hover:bg-secondary-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <BaseIcon icon="angle-double-left" class="w-4 h-4" />
        </button>
        <button 
          @click="currentPage--"
          :disabled="currentPage === 1"
          class="px-2 py-1 text-sm border border-secondary-200 rounded hover:bg-secondary-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <BaseIcon icon="angle-left" class="w-4 h-4" />
        </button>
        
        <span class="px-3 py-1 text-sm">
          {{ currentPage }} / {{ totalPages }}
        </span>
        
        <button 
          @click="currentPage++"
          :disabled="currentPage === totalPages"
          class="px-2 py-1 text-sm border border-secondary-200 rounded hover:bg-secondary-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <BaseIcon icon="angle-right" class="w-4 h-4" />
        </button>
        <button 
          @click="currentPage = totalPages"
          :disabled="currentPage === totalPages"
          class="px-2 py-1 text-sm border border-secondary-200 rounded hover:bg-secondary-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <BaseIcon icon="angle-double-right" class="w-4 h-4" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ContractsList',
  props: {
    contracts: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    pageSize: {
      type: Number,
      default: 10
    }
  },
  data() {
    return {
      searchQuery: '',
      statusFilter: 'all',
      sortColumn: 'startDate',
      sortOrder: 'desc',
      currentPage: 1
    };
  },
  computed: {
    columns() {
      return [
        { key: 'agreementNumber', label: this.$t('sales_dashboard.agreement_number') },
        { key: 'customerName', label: this.$t('sales_dashboard.customer') },
        { key: 'category', label: this.$t('sales_dashboard.category') },
        { key: 'value', label: this.$t('sales_dashboard.value') },
        { key: 'startDate', label: this.$t('sales_dashboard.start_date') },
        { key: 'seller', label: this.$t('sales_dashboard.salesperson') },
        { key: 'status', label: this.$t('sales_dashboard.status') },
        { key: 'productCount', label: this.$t('sales_dashboard.products') }
      ];
    },
    filteredContracts() {
      let result = [...this.contracts];
      
      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        result = result.filter(c => 
          (c.customerName && c.customerName.toLowerCase().includes(query)) ||
          (c.agreementNumber && c.agreementNumber.toLowerCase().includes(query)) ||
          (c.seller && c.seller.toLowerCase().includes(query))
        );
      }
      
      // Status filter
      if (this.statusFilter !== 'all') {
        result = result.filter(c => c.status === this.statusFilter);
      }
      
      // Sorting
      result.sort((a, b) => {
        let aVal = a[this.sortColumn];
        let bVal = b[this.sortColumn];
        
        // Handle value sorting
        if (this.sortColumn === 'value') {
          aVal = a.monthlyValue || a.cashValue || 0;
          bVal = b.monthlyValue || b.cashValue || 0;
        }
        
        // Handle date sorting
        if (this.sortColumn === 'startDate') {
          aVal = new Date(aVal || 0);
          bVal = new Date(bVal || 0);
        }
        
        if (aVal < bVal) return this.sortOrder === 'asc' ? -1 : 1;
        if (aVal > bVal) return this.sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
      
      return result;
    },
    totalContracts() {
      return this.filteredContracts.length;
    },
    totalPages() {
      return Math.ceil(this.totalContracts / this.pageSize);
    },
    displayedContracts() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredContracts.slice(start, start + this.pageSize);
    }
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    statusFilter() {
      this.currentPage = 1;
    },
    contracts() {
      this.currentPage = 1;
    }
  },
  methods: {
    sortBy(column) {
      if (this.sortColumn === column) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        this.sortOrder = 'desc';
      }
    },
    formatCurrency(value) {
      return `${(value || 0).toLocaleString('sv-SE', { maximumFractionDigits: 0 })} kr`;
    },
    formatDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      return d.toISOString().slice(0, 10);
    },
    getCategoryLabel(category) {
      const labels = {
        rental: this.$t('sales_dashboard.category_rental'),
        cash: this.$t('sales_dashboard.category_cash'),
        cash_services: this.$t('sales_dashboard.category_cash_services'),
        financed: this.$t('sales_dashboard.category_financed')
      };
      return labels[category] || category;
    },
    getCategoryClass(category) {
      const classes = {
        rental: 'bg-primary-100 text-primary-700',
        cash: 'bg-accent-100 text-accent-700',
        cash_services: 'bg-yellow-100 text-yellow-700',
        financed: 'bg-blue-100 text-blue-700'
      };
      return classes[category] || 'bg-secondary-100 text-secondary-700';
    },
    getStatusLabel(status) {
      const labels = {
        active: this.$t('sales_dashboard.status_active'),
        active_old: this.$t('sales_dashboard.status_active_old'),
        pending: this.$t('sales_dashboard.status_pending'),
        returned: this.$t('sales_dashboard.status_returned'),
        dismissed: this.$t('sales_dashboard.status_dismissed'),
        extended: this.$t('sales_dashboard.status_extended'),
        redrawn: this.$t('sales_dashboard.status_redrawn')
      };
      return labels[status] || status;
    },
    getStatusClass(status) {
      const classes = {
        active: 'bg-accent-100 text-accent-700',
        active_old: 'bg-accent-50 text-accent-600',
        pending: 'bg-yellow-100 text-yellow-700',
        returned: 'bg-red-100 text-red-700',
        dismissed: 'bg-secondary-200 text-secondary-700',
        extended: 'bg-blue-100 text-blue-700',
        redrawn: 'bg-purple-100 text-purple-700'
      };
      return classes[status] || 'bg-secondary-100 text-secondary-700';
    }
  }
};
</script>

<style scoped>
</style>
